import React, { FunctionComponent } from 'react';
import BasicCard, { WideFormBody } from 'components/Card/Basic';
import Field from 'components/Form/Field';
import PhoneField from 'components/Form/PhoneInput/FormikField';
import SingleSelect from 'components/Form/Select/SingleSelect';
import SingleDatePicker from 'components/Form/DatePicker/Single/SingleDatePicker';
import SingleTimePicker from 'components/Form/TimePicker';
import Flex from 'components/Flex';
import Button from 'components/Button';
import { IReservationValues } from '.';
import { FormikProps } from 'formik';
import { GuestOptions, useCreateReservation } from '../../useCreateReservation';
import UnitTemplate from './UnitTemplate';
import { IPublicAccount } from 'hooks/common/useGetPublicAccount';
import styled from 'styled-components';

export interface IProps extends FormikProps<IReservationValues> {
  account: IPublicAccount;
  guid?: string;
}

const Form: FunctionComponent<IProps> = ({
  handleSubmit,
  setFieldValue,
  isSubmitting,
  values,
  account,
  guid,
}) => {
  const {
    countries,
    regions,
    hasRegions,
    spots,
    maxAllowDayForReservation,
    minEndDate,
    maxEndDate,
    minStartTime,
    maxStartTime,
    minEndTime,
    maxEndTime,
  } = useCreateReservation({
    guid,
    values,
    setFieldValue,
    parking_can_reservation_days: account.parking_can_reservation_days,
    parking_time_limit: account.parking_time_limit,
    parking_start_working: account.parking_start_working,
    parking_end_working: account.parking_end_working,
    location_country_id: account.location_country_id,
  });

  return (
    <form onSubmit={handleSubmit}>
      <BasicCard title="Your Contact Information" className="mt16">
        <WideFormBody>
          <Field name="first_name" label="Your first name" />
          <Field name="last_name" label="Your last name" />
          <PhoneField name="phone_number" label="Your phone number" data-lpignore="true" />
        </WideFormBody>
      </BasicCard>
      <BasicCard title="Your Vehicle Information" className="mt16">
        <WideFormBody>
          <Field
            component={SingleSelect}
            name="location_country_id"
            placeholder="Choose a country"
            label="Your license plate country"
            options={countries}
            alphabeticallySort={false}
          />
          <Field
            name="location_region_id"
            component={SingleSelect}
            label="Your license plate province"
            placeholder="Choose a province"
            options={regions}
            isDisabled={!values.location_country_id || !hasRegions}
            validate={(value: number) => (hasRegions && !value ? 'Required' : undefined)}
          />
          <Field name="license_plate" label="Your license plate" />
          <Field
            name="guest"
            component={SingleSelect}
            label="You are a"
            placeholder="Choose"
            options={GuestOptions}
          />
          {values.guest === 'vendor' && <Field name="company" label="Company name" />}
        </WideFormBody>
      </BasicCard>
      <BasicCard title="Who Are You Visiting" className="mt16">
        <WideFormBody>
          <UnitTemplate
            guid={guid}
            buildingPlaceholder="Choose a building"
            unitPlaceholder="Choose a unit"
            values={values}
            setFieldValue={setFieldValue}
          />
          <Flex direction="column">
            <DateWrap>
              <TimeFieldWrap flex={1}>
                <Field
                  className="mr10"
                  name="start_date"
                  label="Parking start date"
                  component={SingleDatePicker}
                  dayPickerProps={{
                    disabledDays: {
                      before: new Date(),
                      after: maxAllowDayForReservation,
                    },
                  }}
                />
              </TimeFieldWrap>
              <TimeFieldWrap flex={1}>
                <Field
                  name="start_time"
                  label="Parking start time"
                  min={minStartTime}
                  max={maxStartTime}
                  component={SingleTimePicker}
                  hourInputProps={{
                    isDisabled: !values.start_date,
                  }}
                  minuteInputProps={{
                    isDisabled: !values.start_time_hours,
                  }}
                />
              </TimeFieldWrap>
            </DateWrap>
            <DateWrap>
              <TimeFieldWrap flex={1}>
                <Field
                  className="mr10"
                  name="end_date"
                  label="Parking end date"
                  component={SingleDatePicker}
                  disabled={
                    !values.start_date || !values.start_time_hours || !values.start_time_minutes
                  }
                  dayPickerProps={{
                    disabledDays: {
                      before: minEndDate,
                      after: maxEndDate,
                    },
                  }}
                />
              </TimeFieldWrap>
              <TimeFieldWrap flex={1}>
                <Field
                  name="end_time"
                  label="Parking end time"
                  min={minEndTime}
                  max={maxEndTime}
                  component={SingleTimePicker}
                  hourInputProps={{
                    isDisabled:
                      !values.start_date ||
                      !values.end_date ||
                      !values.start_time_hours ||
                      !values.start_time_minutes,
                  }}
                  minuteInputProps={{
                    isDisabled: !values.end_time_hours,
                  }}
                />
              </TimeFieldWrap>
            </DateWrap>
          </Flex>
          <Field
            component={SingleSelect}
            name="parking_spot_id"
            placeholder="Choose a spot"
            label="Select your parking spot"
            options={spots}
            menuPlacement="top"
          />
        </WideFormBody>

        <Flex justifyContent="center">
          <Button
            type="submit"
            disabled={isSubmitting}
            data-test="button-submit-form"
            className="mb24"
          >
            Register
          </Button>
        </Flex>
      </BasicCard>
    </form>
  );
};

export default Form;

const TimeFieldWrap = styled(Flex)`
  & > div {
    width: 100%;
  }
`;

const DateWrap = styled(Flex)`
  ${(props) => props.theme.media.sm`
    flex-direction: column;
  `}
`;
